import "./EventParticipants.css";
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuthStore } from "../Services/authStore";
import { toastError } from "../Services/toaster";

// ─────────────────────────────────────────────────────────
//  EVENT PARTICIPANTS COMPONENT
// ─────────────────────────────────────────────────────────
function EventParticipants() {
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");

  const token = useAuthStore((state) => state.token);
  const API_URL = process.env.REACT_APP_API_URL;

  const authHeaders = useCallback(
    () => ({
      Authorization: `Bearer ${token}`,
      accept: "application/json",
    }),
    [token],
  );

  // ─── GET /events/list - Fetch Events ──────────────────
  const fetchEvents = useCallback(async () => {
    try {
      setLoadingEvents(true);
      const response = await axios.get(`${API_URL}/events/list`, {
        headers: authHeaders(),
      });

      const data = response.data?.data || response.data || [];
      setEvents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to load events:", err.response?.data);
      toastError("Failed to load events");
      setEvents([]);
    } finally {
      setLoadingEvents(false);
    }
  }, [API_URL, authHeaders]);

  // ─── GET /event-registrations/event/{event_id} ────────
  const fetchParticipants = useCallback(
    async (eventId) => {
      if (!eventId) {
        setParticipants([]);
        return;
      }

      try {
        setLoading(true);

        const response = await axios.get(
          `${API_URL}/event-registrations/event/${eventId}`,
          { headers: authHeaders() },
        );

        const data = response.data?.data || response.data || [];

        // Map backend fields to the shape used by this UI
        const mapped = (Array.isArray(data) ? data : []).map((r) => ({
          id: r.id,
          type: r.is_member === true || r.is_member === 1 ? "Member" : "Guest",
          is_member: r.is_member === true || r.is_member === 1,
          title: r.title || "",
          first_name: r.first_name || "",
          last_name: r.last_name || "",
          email: r.email || "",
          phone: r.phone || "",
          nationality: r.nationality || "",
          order_id: r.order_id || "",
          registration_number: r.order_id || "", // backend uses order_id
          member_id: r.member_id || 0,
          guest_id: r.guest_id || 0,
          price: r.price || "0",
          currency: r.currency || "USD",
          diet_pref: r.dietary_preference || "",
          dietary_information: r.dietary_information || "",
          additional_information: r.additional_information || "",
          remarks: r.remarks || "",
          payment_status: r.payment_status || "PENDING",
          order_status: r.order_status || "ACTIVE",
          registered_at: r.created_at || "",
          is_active: true,
          grade: "",
          rank: "",
        }));

        // Sort newest first (by id desc)
        mapped.sort((a, b) => (b.id || 0) - (a.id || 0));

        setParticipants(mapped);
      } catch (err) {
        console.error("Failed to load participants:", err.response?.data);
        toastError("Failed to load participants");
        setParticipants([]);
      } finally {
        setLoading(false);
      }
    },
    [API_URL, authHeaders],
  );

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleEventChange = (e) => {
    const eventId = e.target.value;
    setSelectedEvent(eventId);
    setSearchTerm("");
    setTypeFilter("All");
    fetchParticipants(eventId);
  };

  // ── Filter participants ──
  const filteredParticipants = participants.filter((p) => {
    const matchType = typeFilter === "All" || p.type === typeFilter;
    const search = searchTerm.toLowerCase();
    const matchSearch =
      (p.first_name || "").toLowerCase().includes(search) ||
      (p.last_name || "").toLowerCase().includes(search) ||
      (p.email || "").toLowerCase().includes(search) ||
      (p.phone || "").includes(search) ||
      (p.registration_number || "").toLowerCase().includes(search) ||
      (p.order_id || "").toLowerCase().includes(search);
    return matchType && matchSearch;
  });

  // ── Stats ──
  const totalCount = participants.length;
  const memberCount = participants.filter((p) => p.type === "Member").length;
  const guestCount = participants.filter((p) => p.type === "Guest").length;

  // ── Format Helpers ──
  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getSelectedEventInfo = () => {
    return events.find((e) => e.id === parseInt(selectedEvent));
  };

  // ─── Render ──────────────────────────────────────────
  return (
    <div className="ep-page">
      {/* ─── Top Bar ─── */}
      <div className="ep-page-header">
        <div>
          <h1 className="ep-page-title">Event Participants</h1>
          <p className="ep-page-subtitle">
            View members and guests registered for each event
          </p>
        </div>
        <div className="ep-page-actions">
          <select
            className="ep-event-select"
            value={selectedEvent}
            onChange={handleEventChange}
            disabled={loadingEvents}
          >
            <option value="">
              {loadingEvents ? "Loading events..." : "Select Event"}
            </option>
            {events.map((event) => (
              <option key={event.id} value={event.id}>
                {(event.event_title || event.title) +
                  " — " +
                  formatDate(event.event_date || event.date)}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ─── Event Info + Stats ─── */}
      {selectedEvent && (
        <div className="ep-info-bar">
          <div className="ep-event-info">
            <div className="ep-event-title">
              {getSelectedEventInfo()?.event_title ||
                getSelectedEventInfo()?.title}
            </div>
            <div className="ep-event-meta">
              📅{" "}
              {formatDate(
                getSelectedEventInfo()?.event_date ||
                  getSelectedEventInfo()?.date,
              )}{" "}
              · 📍 {getSelectedEventInfo()?.venue}
            </div>
          </div>
          <div className="ep-stats">
            <div className="ep-stat-pill ep-stat-total">
              <span className="ep-stat-num">{totalCount}</span>
              <span className="ep-stat-lbl">Total</span>
            </div>
            <div className="ep-stat-pill ep-stat-member">
              <span className="ep-stat-num">{memberCount}</span>
              <span className="ep-stat-lbl">Members</span>
            </div>
            <div className="ep-stat-pill ep-stat-guest">
              <span className="ep-stat-num">{guestCount}</span>
              <span className="ep-stat-lbl">Guests</span>
            </div>
          </div>
        </div>
      )}

      {/* ─── Search + Filter ─── */}
      {selectedEvent && (
        <div className="ep-toolbar">
          <div className="ep-search-box">
            <span className="ep-search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search participants..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="ep-filter-tabs">
            {["All", "Member", "Guest"].map((tab) => (
              <button
                key={tab}
                className={`ep-filter-tab ${
                  typeFilter === tab ? "ep-filter-tab-active" : ""
                }`}
                onClick={() => setTypeFilter(tab)}
              >
                {tab}
                {tab === "Member" && (
                  <span className="ep-filter-count">{memberCount}</span>
                )}
                {tab === "Guest" && (
                  <span className="ep-filter-count">{guestCount}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ─── Table ─── */}
      <div className="ep-table-card">
        {!selectedEvent ? (
          <div className="ep-empty-state">
            <div className="ep-empty-icon">🎯</div>
            <div className="ep-empty-title">Select an Event</div>
            <div className="ep-empty-desc">
              Choose an event from the dropdown above to view its participants
            </div>
          </div>
        ) : loading ? (
          <div className="ep-empty-state">
            <p>Loading participants...</p>
          </div>
        ) : filteredParticipants.length === 0 ? (
          <div className="ep-empty-state">
            <div className="ep-empty-icon">👥</div>
            <div className="ep-empty-title">No Participants Found</div>
            <div className="ep-empty-desc">
              {searchTerm
                ? `No participants match "${searchTerm}"`
                : "No members or guests registered for this event yet"}
            </div>
          </div>
        ) : (
          <div className="ep-table-responsive">
            <table className="ep-table">
              <thead>
                <tr>
                  <th className="ep-col-id">#</th>
                  <th className="ep-col-type">Type</th>
                  <th className="ep-col-name">Name</th>
                  <th className="ep-col-contact">Contact</th>
                  <th className="ep-col-reg">Order ID</th>
                  <th className="ep-col-grade">Nationality</th>
                  <th className="ep-col-diet">Diet</th>
                  <th className="ep-col-date">Registered</th>
                </tr>
              </thead>
              <tbody>
                {filteredParticipants.map((p, index) => (
                  <tr key={`${p.type}-${p.id}`}>
                    <td className="ep-col-id">{index + 1}</td>
                    <td className="ep-col-type">
                      <span
                        className={`ep-type-badge ${
                          p.type === "Member"
                            ? "ep-type-member"
                            : "ep-type-guest"
                        }`}
                      >
                        {p.type}
                      </span>
                    </td>
                    <td className="ep-col-name">
                      <div className="ep-full-name">
                        <span className="ep-title">{p.title}</span>
                        <span className="ep-first">{p.first_name}</span>
                        <span className="ep-last">{p.last_name}</span>
                      </div>
                    </td>
                    <td className="ep-col-contact">
                      <div className="ep-email">
                        <a href={`mailto:${p.email}`} className="ep-email-link">
                          {p.email}
                        </a>
                      </div>
                      <div className="ep-phone">{p.phone}</div>
                    </td>
                    <td className="ep-col-reg">
                      {p.order_id ? (
                        <span className="ep-reg-badge">{p.order_id}</span>
                      ) : (
                        <span className="ep-reg-none">—</span>
                      )}
                    </td>
                    <td className="ep-col-grade">
                      {p.nationality ? (
                        <div className="ep-grade-info">
                          <div className="ep-grade">{p.nationality}</div>
                        </div>
                      ) : (
                        <span className="ep-reg-none">—</span>
                      )}
                    </td>
                    <td className="ep-col-diet">
                      {p.diet_pref ? (
                        <span className="ep-diet-badge">{p.diet_pref}</span>
                      ) : (
                        <span className="ep-reg-none">—</span>
                      )}
                    </td>
                    <td className="ep-col-date">
                      {formatDate(p.registered_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default EventParticipants;
